import * as React from "react";
import { Label, AutocompleteSelect, cn, Spinner, Icon } from "@ogt/ui";

export interface SearchOption {
  id: string;
  label: string;
  value: string;
  type?: string;
  group?: string;
}

function getSearchIcon(type?: string) {
  switch (type) {
    case "Hållplats":
      return "pin";

    case "Linje":
      return "busfront";

    case "Linjenät":
      return "map";

    default:
      return "search";
  }
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  const cleanQuery = query.trim();

  if (!cleanQuery) return <>{text}</>;

  const index = text.toLowerCase().indexOf(cleanQuery.toLowerCase());

  if (index === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, index)}
      <strong>{text.slice(index, index + cleanQuery.length)}</strong>
      {text.slice(index + cleanQuery.length)}
    </>
  );
}

function normalizeSearch(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/\s+/g, " ");
}

function damerauLevenshtein(a: string, b: string) {
  const rows = a.length + 1;
  const cols = b.length + 1;

  const d = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let i = 0; i < rows; i++) d[i][0] = i;
  for (let j = 0; j < cols; j++) d[0][j] = j;

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }

  return d[a.length][b.length];
}

function fuzzyStopMatch(option: SearchOption, query: string) {
  const q = normalizeSearch(query);

  if (q.length < 4) return false;
  if (option.type !== "Hållplats") return false;

  const text = normalizeSearch(`${option.label} ${option.value}`);

  if (text.includes(q)) return false;

  const words = text.split(/[\s,()/-]+/).filter(Boolean);

  return words.some((word) => {
    if (word.length < q.length - 1) return false;

    const candidate = word.slice(0, q.length);
    const distance = damerauLevenshtein(q, candidate);

    return distance <= 2;
  });
}

interface MapSearchInputProps {
  value: string;
  options: SearchOption[];
  placeholder?: string;
  onInputChange: (value: string) => void;
  onSelect: (option: SearchOption) => void;
  label: string;
  loading?: boolean;
  emptyContent?: React.ReactNode;
  loadingContent?: React.ReactNode;
  minCharsToOpen?: number;
  closeOnSelect?: boolean;
  openOnFocus?: boolean;
  className?: string;
  dropdownWidthClassName?: string;

  manual?: boolean;
  submitSignal?: string | number | boolean;
  onDraftChange?: (value: string) => void;
}

const MapSearchInput: React.FC<MapSearchInputProps> = ({
  value,
  options,
  placeholder,
  onInputChange,
  onSelect,
  label,
  minCharsToOpen = 1,
  closeOnSelect = true,
  openOnFocus = true,
  className,
  loading,
  emptyContent,
  loadingContent,
  dropdownWidthClassName,

  manual = false,
  submitSignal,
  onDraftChange,
}) => {
  const [selectedId, setSelectedId] = React.useState<string | undefined>();
  const [draft, setDraft] = React.useState<string>(value ?? "");

  React.useEffect(() => {
    if (!manual) return;
    setDraft(value ?? "");
  }, [value, manual]);

  React.useEffect(() => {
    if (!manual) return;
    onInputChange(draft);
  }, [submitSignal, manual, draft, onInputChange]);

  const queryProp = manual ? draft : value;

  React.useEffect(() => {
    if (!queryProp.trim()) {
      setSelectedId(undefined);
    }
  }, [queryProp]);

  const fuzzyIds = React.useMemo(() => {
    const q = queryProp.trim();

    if (!q) return new Set<string>();

    return new Set(
      options.filter((o) => fuzzyStopMatch(o, q)).map((o) => o.id)
    );
  }, [options, queryProp]);

  const acOptions = React.useMemo(
    () =>
      options.map((o) => {
        const isFuzzyHit = fuzzyIds.has(o.id);

        return {
          value: o.id,

          label: isFuzzyHit ? `${o.label} ${queryProp}` : o.label,

          displayLabel: o.label,

          keywords: isFuzzyHit
            ? `${o.value} ${o.label} ${queryProp}`
            : `${o.value} ${o.label}`,

          group: o.group,

          leading: o.type ? (
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted"
              title={o.type}
            >
              <Icon
                name={getSearchIcon(o.type)}
                aria-hidden
                className="size-4"
              />
            </span>
          ) : undefined,
        };
      }),
    [options, fuzzyIds, queryProp]
  );

  function handleQueryChange(next: string) {
    if (manual) {
      setDraft(next);
      onDraftChange?.(next);
    } else {
      onInputChange(next);
    }
  }

  function handleValueChange(next?: string) {
    setSelectedId(next);
    if (!next) return;

    const picked = options.find((o) => o.id === next);
    if (!picked) return;

    if (manual) {
      setDraft(picked.label);
      onDraftChange?.(picked.label);
    } else {
      onInputChange(picked.label);
    }

    onSelect(picked);
  }

  const finalLoadingContent = loadingContent ?? (
    <div className="flex items-center gap-2 px-3 py-2">
      <Spinner className="h-4 w-4" />
      <span>Söker…</span>
    </div>
  );

  const finalEmptyContent = emptyContent ?? (
    <div className="px-3 py-2">
      {queryProp && !loading
        ? "Inga träffar på din sökning."
        : "Börja skriva för att söka."}
    </div>
  );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label>{label}</Label>

      <AutocompleteSelect
        query={queryProp}
        onQueryChange={handleQueryChange}
        value={selectedId}
        onValueChange={handleValueChange}
        options={acOptions}
        placeholder={placeholder}
        openOnFocus={openOnFocus}
        closeOnSelect={closeOnSelect}
        minCharsToOpen={minCharsToOpen}
        dropdownWidthClassName={dropdownWidthClassName}
        loading={loading}
        emptyContent={finalEmptyContent}
        loadingContent={finalLoadingContent}
        renderOption={(opt) => {
          const displayLabel = String(
            (opt as any).displayLabel ?? opt.label ?? ""
          );

          return (
            <div className="flex items-center gap-2">
              <span className="truncate">
                <HighlightMatch text={displayLabel} query={queryProp} />
              </span>
            </div>
          );
        }}
      />
    </div>
  );
};

export default MapSearchInput;
